import { jsxs as _jsxs, Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { Box, Dialog, IconButton, Radio, Typography } from "@mui/material";
import { Info, Trash2, X } from "lucide-react";
import { useSelector } from "react-redux";
import AppButton from "../../../common_components/AppButton";
import ConfirmActionModal from "../../../common_components/ConfirmActionModal";
import MultiSelectInput from "../../../common_components/MultiSelectInput";
import { Dropdown, TextInput } from "../../../common_components/FormInputs";
import { toast } from "../../../common_components/Toast/ToastUtils";
import { selectToken } from "../../../redux/authSlice";
import reportBuilderService from "../../../services/reportBuilder.service";
// ── Options ─────────────────────────────────────────────────────────────
const FREQUENCIES = [
    { value: "day", label: "Daily" },
    { value: "week", label: "Weekly" },
    { value: "bi_weekly", label: "Bi-Weekly" },
    { value: "month", label: "Monthly" },
];
/** Frequencies whose runs are picked by weekday. */
const isWeekly = (f) => f === "week" || f === "bi_weekly";
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WEEKDAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
/** 1-28 only, so every month has the day (legacy overflowed 29-31 into the next month). */
const MONTH_DAYS = Array.from({ length: 28 }, (_, i) => i + 1);
/** Every schedule runs on Eastern time. */
const TIMEZONE = "America/New_York";
const TIMEZONE_LABEL = "(GMT-05:00) Eastern Time";
const pad = (n) => String(n).padStart(2, "0");
const fmtTime12 = (hhmm) => {
    const [h, m] = hhmm.split(":").map(Number);
    return `${h % 12 || 12}:${pad(m)} ${h < 12 ? "AM" : "PM"}`;
};
/** On the hour, 1 AM → 11 PM, then 12 AM. */
const TIME_OPTIONS = Array.from({ length: 24 }, (_, i) => {
    const value = `${pad((i + 1) % 24)}:00`;
    return { value, label: fmtTime12(value) };
});
const ordinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};
/** "a, b and c" */
const joinAnd = (items) => items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
// ── Calendar dates (local midnight, no time-of-day) ─────────────────────
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const lastOfMonth = (y, m) => new Date(y, m + 1, 0).getDate();
const isMonthEnd = (d) => d.getDate() === lastOfMonth(d.getFullYear(), d.getMonth());
/** Shift by n months, clamping the day; a month-end stays a month-end. */
const addMonths = (d, n) => {
    const y = d.getFullYear();
    const m = d.getMonth() + n;
    const last = lastOfMonth(y, m);
    return new Date(y, m, isMonthEnd(d) ? last : Math.min(d.getDate(), last));
};
const fmtDate = (d) => `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
const fmtShort = (d) => `${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
/** "07/01/2026 - 07/05/2026" → dates; null when it does not parse. */
const parseRange = (range) => {
    const m = range.match(/(\d{2})\/(\d{2})\/(\d{4})\s*-\s*(\d{2})\/(\d{2})\/(\d{4})/);
    if (!m)
        return null;
    const [, sm, sd, sy, em, ed, ey] = m.map(Number);
    return { start: new Date(sy, sm - 1, sd), end: new Date(ey, em - 1, ed) };
};
/** Today's date and minutes-past-midnight in `tz`. */
const nowIn = (tz) => {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
    }).formatToParts(new Date());
    const get = (type) => Number(parts.find((p) => p.type === type)?.value);
    return {
        today: new Date(get("year"), get("month") - 1, get("day")),
        minutes: get("hour") * 60 + get("minute"),
    };
};
/** Mon = 0 … Sun = 6. */
const weekdayOf = (d) => (d.getDay() + 6) % 7;
/**
 * The next `count` run dates (Eastern) for this frequency. Bi-weekly matches
 * the cron: each weekday first runs on its next occurrence, then every 14 days.
 */
const nextRunDates = (frequency, weekdays, monthDays, time, count) => {
    if (isWeekly(frequency) && !weekdays.length)
        return [];
    if (frequency === "month" && !monthDays.length)
        return [];
    const { today, minutes } = nowIn(TIMEZONE);
    const [h, m] = time.split(":").map(Number);
    const runMinutes = h * 60 + m;
    const runs = [];
    /** Bi-weekly: day offset of each weekday's first run. */
    const firstRun = {};
    for (let i = 0; runs.length < count && i < 400; i += 1) {
        if (i === 0 && runMinutes <= minutes)
            continue;
        const d = addDays(today, i);
        const wd = weekdayOf(d);
        let match = false;
        if (frequency === "day")
            match = true;
        else if (frequency === "week")
            match = weekdays.includes(wd);
        else if (frequency === "bi_weekly" && weekdays.includes(wd)) {
            if (firstRun[wd] === undefined)
                firstRun[wd] = i;
            match = (i - firstRun[wd]) % 14 === 0;
        }
        else if (frequency === "month")
            match = monthDays.includes(d.getDate());
        if (match)
            runs.push(d);
    }
    return runs;
};
const sameDay = (a, b) => a.getTime() === b.getTime();
const daysBetween = (a, b) => Math.round((b.getTime() - a.getTime()) / 86400000);
const firstOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const lastOfMonthDate = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
/** Mon = 1 … Sun = 7. */
const isoWeekday = (d) => d.getDay() || 7;
/** The next Monday (1) / Sunday (7) strictly after d - PHP "next monday". */
const nextIsoWeekday = (d, wd) => addDays(d, (wd - isoWeekday(d) + 7) % 7 || 7);
/** The Sunday strictly before d - PHP "last sunday". */
const lastSundayBefore = (d) => addDays(d, -(isoWeekday(d) % 7 || 7));
const COMPARE_PRESETS = ["month_over_month", "previous_year", "previous_period"];
/** The date picker's compare presets (CustomDatePicker getCompareRanges). */
const compareRangeFor = (preset, r) => {
    if (preset === "previous_period") {
        const end = addDays(r.start, -1);
        return { start: addDays(end, -daysBetween(r.start, r.end)), end };
    }
    const months = preset === "month_over_month" ? 1 : 12;
    const shift = (d) => {
        const last = lastOfMonth(d.getFullYear(), d.getMonth() - months);
        return new Date(d.getFullYear(), d.getMonth() - months, Math.min(d.getDate(), last));
    };
    // A month's first / last day stays the first / last day.
    return {
        start: r.start.getDate() === 1 ? firstOfMonth(shift(r.start)) : shift(r.start),
        end: isMonthEnd(r.end) ? lastOfMonthDate(shift(r.end)) : shift(r.end),
    };
};
/** Legacy decremental: one frequency step, each end only once its step is over. */
const decrementalRange = (frequency, saved, weekDimension, today) => {
    const yesterday = addDays(today, -1);
    if (frequency === "day") {
        const nextStart = addDays(saved.start, 1);
        const nextEnd = addDays(saved.end, 1);
        return {
            start: nextStart <= yesterday ? nextStart : saved.start,
            end: nextEnd <= yesterday ? nextEnd : saved.end,
        };
    }
    if (frequency === "month") {
        const nextStart = new Date(saved.start.getFullYear(), saved.start.getMonth() + 1, 1);
        const nextEnd = new Date(saved.end.getFullYear(), saved.end.getMonth() + 2, 0);
        return {
            start: lastOfMonthDate(nextStart) <= yesterday ? nextStart : saved.start,
            end: nextEnd <= yesterday ? nextEnd : saved.end,
        };
    }
    // Weekly without a Week dimension: always the last 7 days.
    if (frequency === "week" && !weekDimension)
        return { start: addDays(today, -7), end: yesterday };
    // Monday → Sunday weeks, one (bi-weekly: two) at a time once the new end's Sunday has passed.
    let nextStart = saved.start;
    let nextEnd = saved.end;
    for (let i = 0; i < (frequency === "bi_weekly" ? 2 : 1); i += 1) {
        nextStart = nextIsoWeekday(nextStart, 1);
        nextEnd = nextIsoWeekday(nextEnd, 7);
    }
    const start = today <= nextStart ? saved.start : nextStart;
    const end = nextEnd > lastSundayBefore(today) ? saved.end : nextEnd;
    return start > end ? { start: saved.start, end: saved.end } : { start, end };
};
/**
 * The dates a run on `runDate` generates (the cron saves them on the report,
 * so the next run starts from them):
 * rtd  - saved start → the day before the run;
 * mtd  - 1st of the day-before's month → the day before the run, so a run on
 *        the 1st covers the whole previous month;
 * decremental - the saved range moved one frequency step once it is complete;
 *        an unchanged end keeps the saved range and compare.
 * Compare - a preset (MoM / Previous Year / Previous Period) is worked out again
 * from the new range; a custom one moves by as many days as the main range.
 */
const runDatesFor = (period, frequency, saved, weekDimension, runDate) => {
    const yesterday = addDays(runDate, -1);
    let range;
    if (period === "rtd") {
        range = { start: saved.start, end: yesterday < saved.start ? saved.start : yesterday };
    }
    else if (period === "mtd") {
        range = { start: firstOfMonth(yesterday), end: yesterday };
    }
    else {
        range = decrementalRange(frequency, saved, weekDimension, runDate);
        if (sameDay(range.end, saved.end))
            return saved;
    }
    if (!saved.compare)
        return { ...range, compare: null };
    const preset = COMPARE_PRESETS.find((p) => {
        const r = compareRangeFor(p, saved);
        return sameDay(r.start, saved.compare.start) && sameDay(r.end, saved.compare.end);
    });
    if (preset)
        return { ...range, compare: compareRangeFor(preset, range) };
    return {
        ...range,
        compare: {
            start: addDays(saved.compare.start, daysBetween(saved.start, range.start)),
            end: addDays(saved.compare.end, daysBetween(saved.end, range.end)),
        },
    };
};
// ── Defaults ────────────────────────────────────────────────────────────
const DEFAULTS = {
    frequency: "day",
    weekdays: [0],
    month_days: [1],
    time: "09:00",
    period: "rtd",
    internal_user_ids: [],
    external_user_ids: [],
    contact_ids: [],
};
// ── Styles ──────────────────────────────────────────────────────────────
const PINK = "#FF008E";
const PINK_BG = "#FFF1F8";
const cardSx = {
    bgcolor: "#FFFFFF",
    border: "1px solid #EAECF0",
    borderRadius: "12px",
    p: 2.5,
};
const sectionTitleSx = { fontSize: "16px !important", fontWeight: 600, color: "#101828" };
const fieldLabelSx = { fontSize: "14px !important", fontWeight: 500, color: "#344054", mb: 0.75 };
const helperSx = { fontSize: "12px !important", color: "#667085", mt: 0.75 };
const chipButtonSx = (active) => ({
    minWidth: 44,
    height: 36,
    px: 1,
    borderRadius: "8px",
    border: `1px solid ${active ? PINK : "#D0D5DD"}`,
    bgcolor: active ? PINK_BG : "#FFFFFF",
    color: active ? PINK : "#344054",
    fontSize: "13px",
    fontWeight: active ? 600 : 500,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "all 120ms",
    "&:hover": { borderColor: PINK },
});
// ── Component ───────────────────────────────────────────────────────────
const ScheduleReportModal = ({ open, target, userOptions, loading = false, deleting = false, onSubmit, onDelete, onClose, }) => {
    const token = useSelector(selectToken) || "";
    const [form, setForm] = useState(DEFAULTS);
    const [showValidation, setShowValidation] = useState(false);
    const [contactOptions, setContactOptions] = useState([]);
    /** True while the saved schedule is being read - the form waits for it. */
    const [fetching, setFetching] = useState(false);
    /** The report already has a saved schedule - shows the footer's Delete. */
    const [hasSchedule, setHasSchedule] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    // Every open starts clean, so a cancelled attempt never leaks into the next.
    useEffect(() => {
        if (open) {
            setForm(DEFAULTS);
            setShowValidation(false);
            setHasSchedule(false);
            setConfirmDelete(false);
        }
    }, [open]);
    // Per open: the saved schedule (prefills the form) and External's options,
    // the report's client's active contacts.
    const reportId = open ? target?.report_id : undefined;
    useEffect(() => {
        setContactOptions([]);
        if (!reportId)
            return;
        let cancelled = false;
        (async () => {
            setFetching(true);
            try {
                const res = await reportBuilderService.getReportSchedule(reportId, token);
                if (!res?.data?.success)
                    throw new Error(res?.data?.message || res?.response?.data?.message);
                const s = res.data.data;
                if (!cancelled && s) {
                    setHasSchedule(true);
                    setForm({
                        frequency: s.frequency,
                        weekdays: s.weekdays?.length ? s.weekdays : DEFAULTS.weekdays,
                        month_days: s.month_days?.length ? s.month_days : DEFAULTS.month_days,
                        time: s.time,
                        period: s.period,
                        internal_user_ids: s.internal_user_ids || [],
                        external_user_ids: s.external_user_ids || [],
                        contact_ids: s.contact_ids || [],
                    });
                }
            }
            catch (err) {
                if (!cancelled)
                    toast.error(err?.message || "Could not load the saved schedule.");
            }
            finally {
                if (!cancelled)
                    setFetching(false);
            }
        })();
        (async () => {
            try {
                const res = await reportBuilderService.listReportContacts(reportId, token);
                if (!res?.data?.success)
                    throw new Error(res?.data?.message || res?.response?.data?.message);
                if (!cancelled) {
                    setContactOptions((res.data.data || []).map((c) => ({
                        value: c.contact_id,
                        label: c.name || c.email,
                        // Second line only when the name is shown above it.
                        description: c.name ? c.email : undefined,
                    })));
                }
            }
            catch (err) {
                if (!cancelled)
                    toast.error(err?.message || "Could not load the client's contacts.");
            }
        })();
        return () => {
            cancelled = true;
            setFetching(false);
        };
    }, [reportId, token]);
    const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
    const toggleIn = (list, n) => list.includes(n) ? list.filter((x) => x !== n) : [...list, n].sort((a, b) => a - b);
    const saved = useMemo(() => {
        const range = target ? parseRange(target.date_range) : null;
        if (!range)
            return null;
        return { ...range, compare: target?.compare_date_range ? parseRange(target.compare_date_range) : null };
    }, [target]);
    const weekDimension = Boolean(target?.dimensions?.some((d) => d.toLowerCase() === "week"));
    const runs = useMemo(() => nextRunDates(form.frequency, form.weekdays, form.month_days, form.time, 3), [form.frequency, form.weekdays, form.month_days, form.time]);
    const fmtRange = (r) => `${fmtDate(r.start)} - ${fmtDate(r.end)}`;
    /** First upcoming run's dates for a period card. */
    const previewFor = (period) => {
        if (!saved || !runs.length)
            return null;
        const r = runDatesFor(period, form.frequency, saved, weekDimension, runs[0]);
        return { range: fmtRange(r), compare: r.compare ? fmtRange(r.compare) : null };
    };
    /** Each upcoming run starts from the dates the run before it saved. */
    const runRanges = useMemo(() => {
        if (!saved)
            return [];
        let current = saved;
        return runs.map((run) => (current = runDatesFor(form.period, form.frequency, current, weekDimension, run)));
    }, [saved, runs, form.period, form.frequency, weekDimension]);
    const unit = { day: "day", week: "week", bi_weekly: "two weeks", month: "month" }[form.frequency];
    const periods = [
        {
            value: "rtd",
            title: "Report to date",
            description: "Keeps the report's start date and ends the day before each run.",
        },
        {
            value: "mtd",
            title: "Month to date",
            description: "From the 1st of the month to the day before each run. A run on the 1st covers the full previous month.",
        },
        {
            value: "decremental",
            title: "Date adjustment",
            description: form.frequency === "week" && !weekDimension
                ? "Covers the last 7 days before each run."
                : `Moves the saved date range forward by ${unit === "two weeks" ? unit : `one ${unit}`} on each run, once that ${unit === "two weeks" ? "period" : unit} is complete.`,
        },
    ];
    // ── Validation ──
    const weekdaysError = isWeekly(form.frequency) && !form.weekdays.length;
    const monthDaysError = form.frequency === "month" && !form.month_days.length;
    const recipientsError = !form.internal_user_ids.length && !form.external_user_ids.length && !form.contact_ids.length;
    const invalid = weekdaysError || monthDaysError || recipientsError;
    const handleSubmit = () => {
        if (invalid) {
            setShowValidation(true);
            return;
        }
        onSubmit(form);
    };
    // ── Summary ──
    const whenText = form.frequency === "day"
        ? "Every day"
        : form.frequency === "week"
            ? `Every ${joinAnd(form.weekdays.map((d) => WEEKDAY_NAMES[d]))}`
            : form.frequency === "bi_weekly"
                ? `Every other ${joinAnd(form.weekdays.map((d) => WEEKDAY_NAMES[d]))}`
                : `On the ${joinAnd(form.month_days.map(ordinal))} of every month`;
    const countText = (n, one, many) => `${n} ${n === 1 ? one : many}`;
    const recipientParts = [
        form.internal_user_ids.length &&
            countText(form.internal_user_ids.length, "internal user", "internal users"),
        form.contact_ids.length && countText(form.contact_ids.length, "client contact", "client contacts"),
        form.external_user_ids.length &&
            countText(form.external_user_ids.length, "additional external user", "additional external users"),
    ].filter(Boolean);
    const summary = (_jsxs(_Fragment, { children: [_jsxs("b", { children: [whenText, " at ", fmtTime12(form.time), " Eastern"] }), ",", " ", form.period === "rtd" && (_jsxs(_Fragment, { children: ["generate the report from ", _jsxs("b", { children: [saved ? fmtDate(saved.start) : "its start date", " up to the day before"] })] })), form.period === "mtd" && (_jsxs(_Fragment, { children: ["generate the report from ", _jsx("b", { children: "the 1st of the month up to the day before" })] })), form.period === "decremental" && (_jsx(_Fragment, { children: form.frequency === "week" && !weekDimension ? (_jsxs(_Fragment, { children: ["generate the report for ", _jsx("b", { children: "the last 7 days" })] })) : (_jsxs(_Fragment, { children: ["move the saved date range ", _jsxs("b", { children: ["forward by one ", unit] }), " and generate the report"] })) })), " ", recipientParts.length ? (_jsxs(_Fragment, { children: ["and send it to ", _jsx("b", { children: joinAnd(recipientParts) }), "."] })) : (_jsx(_Fragment, { children: "but no recipients are selected yet." }))] }));
    const busy = loading || deleting;
    const bodyDisabled = busy || fetching;
    return (_jsxs(_Fragment, { children: [_jsxs(Dialog, { open: open, onClose: busy ? undefined : onClose, maxWidth: false, sx: { zIndex: 2000 }, slotProps: {
                    paper: {
                        sx: {
                            width: 1260,
                            maxWidth: "calc(100% - 32px)",
                            height: "calc(100vh - 48px)",
                            maxHeight: 880,
                            m: 2,
                            borderRadius: "16px",
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                            bgcolor: "#F2F4F7",
                        },
                    },
                }, children: [_jsxs(Box, { sx: {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                            px: 3,
                            py: 2,
                            bgcolor: "#FFFFFF",
                            borderBottom: "1px solid #EAECF0",
                            flexShrink: 0,
                        }, children: [_jsxs(Box, { sx: { minWidth: 0 }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.25 }, children: [_jsx(Typography, { sx: { fontSize: "20px !important", fontWeight: 600, color: "#101828" }, children: "Schedule Report" }), hasSchedule && (_jsxs(Box, { sx: {
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: 0.75,
                                                    px: 1.25,
                                                    py: 0.25,
                                                    borderRadius: "999px",
                                                    bgcolor: "#ECFDF3",
                                                    border: "1px solid #ABEFC6",
                                                    color: "#067647",
                                                    fontSize: "12px",
                                                    fontWeight: 600,
                                                    whiteSpace: "nowrap",
                                                }, children: [_jsx(Box, { sx: { width: 6, height: 6, borderRadius: "50%", bgcolor: "#17B26A" } }), "Scheduled"] }))] }), target && (_jsxs(Typography, { title: `${target.report_name}${target.client_name ? ` - ${target.client_name}` : ""}`, sx: {
                                            fontSize: "14px !important",
                                            color: "#475467",
                                            mt: 0.25,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }, children: [_jsx(Box, { component: "span", sx: { fontWeight: 600, color: "#344054" }, children: target.report_name }), target.client_name && _jsxs(_Fragment, { children: [" - ", target.client_name] })] }))] }), _jsx(IconButton, { "aria-label": "Close", onClick: onClose, disabled: busy, sx: { color: "#667085", flexShrink: 0, "&:hover": { bgcolor: "#F2F4F7", color: "#101828" } }, children: _jsx(X, { size: 20 }) })] }), _jsxs(Box, { sx: {
                            flex: 1,
                            minHeight: 0,
                            overflowY: "auto",
                            p: 3,
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 320px" },
                            gap: 2,
                            alignItems: "start",
                        }, children: [_jsxs(Box, { sx: {
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                    minWidth: 0,
                                    opacity: fetching ? 0.5 : 1,
                                    pointerEvents: bodyDisabled ? "none" : "auto",
                                    transition: "opacity 150ms",
                                }, children: [_jsxs(Box, { sx: { ...cardSx, py: 1.5, display: "flex", gap: 1.25, alignItems: "flex-start" }, children: [_jsx(Box, { sx: { color: "#667085", mt: "2px", flexShrink: 0 }, children: _jsx(Info, { size: 16 }) }), _jsx(Typography, { sx: { fontSize: "13px !important", color: "#475467", lineHeight: 1.5 }, children: "The report is sent automatically to the selected recipients at the chosen frequency and time. Each run refreshes the report's date range before it is sent." })] }), _jsxs(Box, { sx: cardSx, children: [_jsx(Typography, { sx: { ...sectionTitleSx, mb: 2 }, children: "When" }), _jsxs(Box, { sx: {
                                                    display: "grid",
                                                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                                    columnGap: 2,
                                                    rowGap: 2.5,
                                                }, children: [_jsxs(Box, { children: [_jsx(Typography, { sx: fieldLabelSx, children: "Delivery frequency" }), _jsx(Box, { sx: {
                                                                    display: "flex",
                                                                    p: "4px",
                                                                    bgcolor: "#F2F4F7",
                                                                    borderRadius: "10px",
                                                                    gap: "4px",
                                                                }, children: FREQUENCIES.map((f) => {
                                                                    const active = form.frequency === f.value;
                                                                    return (_jsx(Box, { component: "button", type: "button", onClick: () => set("frequency", f.value), sx: {
                                                                            flex: 1,
                                                                            height: 36,
                                                                            border: "none",
                                                                            borderRadius: "8px",
                                                                            bgcolor: active ? "#FFFFFF" : "transparent",
                                                                            boxShadow: active
                                                                                ? "0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06)"
                                                                                : "none",
                                                                            color: active ? "#101828" : "#667085",
                                                                            fontSize: "14px",
                                                                            fontWeight: active ? 600 : 500,
                                                                            cursor: "pointer",
                                                                            fontFamily: "inherit",
                                                                        }, children: f.label }, f.value));
                                                                }) })] }), _jsxs(Box, { children: [_jsx(Typography, { sx: fieldLabelSx, children: "Run on" }), form.frequency === "day" && (_jsx(Box, { sx: {
                                                                    height: 44,
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    fontSize: "14px",
                                                                    color: "#667085",
                                                                }, children: "Every day" })), isWeekly(form.frequency) && (_jsx(Box, { sx: { display: "flex", flexWrap: "wrap", gap: 0.75 }, children: WEEKDAYS.map((label, i) => (_jsx(Box, { component: "button", type: "button", onClick: () => set("weekdays", toggleIn(form.weekdays, i)), sx: chipButtonSx(form.weekdays.includes(i)), children: label }, label))) })), form.frequency === "month" && (_jsx(Box, { sx: {
                                                                    display: "grid",
                                                                    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                                                                    gap: 0.5,
                                                                }, children: MONTH_DAYS.map((d) => (_jsx(Box, { component: "button", type: "button", onClick: () => set("month_days", toggleIn(form.month_days, d)), sx: { ...chipButtonSx(form.month_days.includes(d)), minWidth: 0, height: 32 }, children: d }, d))) })), showValidation && (weekdaysError || monthDaysError) && (_jsx(Typography, { sx: { ...helperSx, color: "#D92D20" }, children: weekdaysError ? "Pick at least one day." : "Pick at least one day of the month." }))] }), _jsx(Dropdown, { label: "Delivery time", value: form.time, options: TIME_OPTIONS, 
                                                        // Keep clock order (1 AM … 12 AM), not A-Z.
                                                        sortByLabel: false, onChange: (v) => set("time", v), fullWidth: true, maxHeight: 280, usePortal: true }), _jsx(TextInput, { label: "Time zone", value: TIMEZONE_LABEL, fullWidth: true, disabled: true, readOnly: true })] })] }), _jsxs(Box, { sx: cardSx, children: [_jsx(Typography, { sx: sectionTitleSx, children: "Report period" }), _jsx(Typography, { sx: { ...helperSx, mt: 0.25, mb: 2 }, children: "Which dates each scheduled report covers" }), _jsx(Box, { sx: {
                                                    display: "grid",
                                                    gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
                                                    gap: 1.5,
                                                }, children: periods.map((p) => {
                                                    const active = form.period === p.value;
                                                    const preview = previewFor(p.value);
                                                    return (_jsxs(Box, { onClick: () => set("period", p.value), sx: {
                                                            p: 1.75,
                                                            borderRadius: "10px",
                                                            border: `${active ? 2 : 1}px solid ${active ? PINK : "#EAECF0"}`,
                                                            // Keep the content still when the border thickens.
                                                            m: active ? 0 : "1px",
                                                            bgcolor: active ? PINK_BG : "#FFFFFF",
                                                            cursor: "pointer",
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            gap: 0.75,
                                                            // "&:hover": { border: `1px solid ${PINK}` },
                                                        }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(Radio, { checked: active, size: "small", sx: {
                                                                            p: 0,
                                                                            color: "#D0D5DD",
                                                                            "&.Mui-checked": { color: PINK },
                                                                        } }), _jsx(Typography, { sx: { fontSize: "14px !important", fontWeight: 600, color: "#101828" }, children: p.title })] }), _jsx(Typography, { sx: { fontSize: "13px !important", color: "#475467", lineHeight: 1.45, flex: 1 }, children: p.description }), preview && (_jsxs(Box, { sx: {
                                                                    mt: 0.5,
                                                                    px: 1.25,
                                                                    py: 0.5,
                                                                    borderRadius: "6px",
                                                                    bgcolor: active ? "#FFFFFF" : "#F2F4F7",
                                                                    fontSize: "12px",
                                                                    color: "#475467",
                                                                    whiteSpace: "nowrap",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                }, children: ["Range: ", preview.range, preview.compare && (_jsxs(_Fragment, { children: [_jsx("br", {}), "Compare: ", preview.compare] }))] }))] }, p.value));
                                                }) })] }), _jsxs(Box, { sx: cardSx, children: [_jsx(Typography, { sx: { ...sectionTitleSx, mb: 2 }, children: "Recipients" }), _jsxs(Box, { sx: {
                                                    display: "grid",
                                                    gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
                                                    gap: 2,
                                                }, children: [_jsx(Box, { sx: { minWidth: 0 }, children: _jsx(MultiSelectInput, { label: "Internal Recipients", name: "internal_user_ids", value: form.internal_user_ids, options: userOptions, onChange: (e) => set("internal_user_ids", e.target.value), placeholder: "Select Recipients", hideSelectAll: true, showSearch: true, maxDisplay: 2 }) }), _jsx(Box, { sx: { minWidth: 0 }, children: _jsx(MultiSelectInput, { label: "External Recipients", name: "contact_ids", value: form.contact_ids, options: contactOptions, onChange: (e) => set("contact_ids", e.target.value), placeholder: "Select Recipients", hideSelectAll: true, showSearch: true, maxDisplay: 2 }) }), _jsx(Box, { sx: { minWidth: 0 }, children: _jsx(MultiSelectInput, { label: "Additional External Recipients", name: "external_user_ids", value: form.external_user_ids, options: userOptions, onChange: (e) => set("external_user_ids", e.target.value), placeholder: "Select Recipients", hideSelectAll: true, showSearch: true, maxDisplay: 2 }) })] }), showValidation && recipientsError && (_jsx(Typography, { sx: { ...helperSx, color: "#D92D20" }, children: "Pick at least one recipient." }))] })] }), _jsxs(Box, { sx: { ...cardSx, position: { md: "sticky" }, top: 0 }, children: [_jsxs(Typography, { sx: {
                                            fontSize: "12px !important",
                                            fontWeight: 600,
                                            letterSpacing: "0.06em",
                                            color: "#667085",
                                            mb: 1.5,
                                        }, children: ["NEXT ", runs.length || "", " UPCOMING SCHEDULES"] }), _jsxs(Box, { sx: { borderTop: "1px solid #EAECF0", mt: 2, pt: 2 }, children: [runs.length === 0 && (_jsx(Typography, { sx: { fontSize: "13px !important", color: "#667085" }, children: "Pick a day to see the upcoming runs." })), runs.map((run, i) => {
                                                const r = runRanges[i] ?? null;
                                                return (_jsx(Box, { sx: {
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        gap: 1,
                                                        py: 0.6,
                                                        fontSize: "13px",
                                                    }, children: _jsxs(Box, { component: "span", sx: { color: "#101828", fontSize: "14px !important", fontWeight: 600 }, children: [WEEKDAYS[weekdayOf(run)], ", ", fmtDate(run), " \u00B7 ", fmtTime12(form.time)] }) }, run.getTime()));
                                            })] })] })] }), _jsxs(Box, { sx: {
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            px: 3,
                            py: 2,
                            bgcolor: "#FFFFFF",
                            borderTop: "1px solid #EAECF0",
                            flexShrink: 0,
                            justifyContent: "flex-end",
                        }, children: [hasSchedule && (_jsx(AppButton, { variant: "outlined", onClick: () => setConfirmDelete(true), disabled: busy || fetching, startIcon: _jsx(Trash2, { size: 16 }), className: "all-Font-Sizes", sx: {
                                    textTransform: "none",
                                    whiteSpace: "nowrap",
                                    color: "#D92D20",
                                    borderColor: "#FDA29B",
                                    "&:hover": { borderColor: "#D92D20", bgcolor: "#FEF3F2" },
                                }, children: "Delete Schedule" })), _jsx(AppButton, { variant: "contained", onClick: handleSubmit, loading: loading, disabled: fetching || deleting, className: "all-Font-Sizes", sx: {
                                    // ml: "auto",
                                    minWidth: 180,
                                    bgcolor: PINK,
                                    textTransform: "none",
                                    whiteSpace: "nowrap",
                                    "&:hover": { bgcolor: "#E6007A" },
                                    gap: 1,
                                }, children: hasSchedule ? "Update Schedule" : "Schedule report" })] })] }), _jsx(ConfirmActionModal
            // Closes with the modal - the parent closes it once the delete succeeds.
            , { 
                // Closes with the modal - the parent closes it once the delete succeeds.
                open: open && confirmDelete, title: "Delete schedule", description: `"${target?.report_name ?? ""}" will no longer be sent automatically. This cannot be undone.`, confirmText: "Delete", isDeleteIcon: true, loading: deleting, onConfirm: onDelete, onClose: () => setConfirmDelete(false) })] }));
};
export default ScheduleReportModal;
